import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { useNavigate } from 'react-router';
import { useAppContext } from '../../contexts/AppContext';
import { fetchHomeDashboard } from '../../services/homeDashboardService';
import { getCategoryPresentation } from '../../components/CategorySlider/CategorySlider';

export default function Categories() {
  const navigate = useNavigate();
  const { user } = useAppContext();
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const userId = useMemo(
    () => user?.user_id ?? user?.id ?? user?.customer_id ?? '',
    [user]
  );

  useEffect(() => {
    let isMounted = true;

    const loadCategories = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const response = await fetchHomeDashboard({ userId });

        if (!isMounted) {
          return;
        }

        setCategories(response.categories || []);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setLoadError(error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadCategories();

    return () => {
      isMounted = false;
    };
  }, [userId]);

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 148px)',
        px: { xs: 1.5, sm: 2 },
        pt: 2,
        pb: 12,
      }}
    >
      <Box sx={{ maxWidth: 980, mx: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            mb: 2.5,
          }}
        >
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              width: 46,
              height: 46,
              borderRadius: '16px',
              background: '#fff',
              boxShadow: '0 12px 24px rgba(17, 24, 39, 0.08)',
            }}
          >
            <ArrowBackRoundedIcon />
          </IconButton>
          <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.6rem', sm: '1.9rem' } }}>
            Categories
          </Typography>
          <Box sx={{ width: 46 }} />
        </Box>

        <Paper
          sx={{
            p: { xs: 2, sm: 2.5 },
            borderRadius: '30px',
            background:
              'linear-gradient(135deg, rgba(255,248,243,0.98) 0%, rgba(255,255,255,0.98) 100%)',
            boxShadow: '0 22px 42px rgba(17, 24, 39, 0.08)',
            mb: 2.5,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              right: -40,
              top: -40,
              width: 180,
              height: 180,
              borderRadius: '999px',
              background:
                'radial-gradient(circle, rgba(255,141,120,0.2) 0%, rgba(255,141,120,0) 72%)',
            }}
          />
          <Typography
            sx={{
              position: 'relative',
              zIndex: 1,
              fontSize: '0.8rem',
              fontWeight: 800,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: '#ff7a67',
              mb: 0.75,
            }}
          >
            Browse faster
          </Typography>
          <Typography
            sx={{
              position: 'relative',
              zIndex: 1,
              fontWeight: 800,
              fontSize: { xs: '1.55rem', sm: '2rem' },
              color: '#202733',
              mb: 0.9,
            }}
          >
            Pick a category and jump straight into your next craving
          </Typography>
          <Typography
            sx={{
              position: 'relative',
              zIndex: 1,
              color: '#7d848f',
              lineHeight: 1.7,
              maxWidth: 620,
            }}
          >
            Explore sweets, savouries, vadam, and more through a richer category
            browser built for quick scanning on both desktop and mobile.
          </Typography>
        </Paper>

        {loadError ? (
          <Alert severity='error' sx={{ mb: 2 }}>
            {loadError.message}
          </Alert>
        ) : null}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                sm: 'repeat(3, minmax(0, 1fr))',
                lg: 'repeat(4, minmax(0, 1fr))',
              },
              gap: { xs: 1.5, sm: 2 },
            }}
          >
            {categories.map((category, index) => {
              const presentation = getCategoryPresentation(category.name);

              return (
                <Paper
                  key={category.category_id || category.name || index}
                  onClick={() => navigate('/', { state: { selectedCategory: category } })}
                  sx={{
                    p: { xs: 2, sm: 2.25 },
                    borderRadius: '28px',
                    background:
                      'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(255,248,244,0.96) 100%)',
                    boxShadow: '0 18px 32px rgba(17, 24, 39, 0.07)',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                    animation: `routeSlideFadeIn 420ms cubic-bezier(0.22, 1, 0.36, 1) ${index * 55}ms both`,
                    transition: 'transform 0.22s ease, box-shadow 0.22s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 24px 38px rgba(17, 24, 39, 0.1)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      right: -24,
                      bottom: -24,
                      width: 120,
                      height: 120,
                      borderRadius: '999px',
                      background:
                        'radial-gradient(circle, rgba(255,199,135,0.25) 0%, rgba(255,199,135,0) 72%)',
                    }}
                  />

                  <Box
                    sx={{
                      width: { xs: 74, sm: 82 },
                      height: { xs: 74, sm: 82 },
                      borderRadius: '999px',
                      background: `radial-gradient(circle at 30% 28%, #fff6bf 0%, ${presentation.halo} 40%, #f4bc37 100%)`,
                      color: presentation.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: `0 14px 28px ${presentation.shadow}`,
                      mb: 1.8,
                      position: 'relative',
                      zIndex: 1,
                    }}
                  >
                    {presentation.icon}
                  </Box>

                  <Typography
                    sx={{
                      position: 'relative',
                      zIndex: 1,
                      fontWeight: 800,
                      fontSize: { xs: '1rem', sm: '1.08rem' },
                      color: '#202733',
                      mb: 0.45,
                    }}
                  >
                    {category.name}
                  </Typography>
                  <Typography
                    sx={{
                      position: 'relative',
                      zIndex: 1,
                      color: '#8a9099',
                      fontWeight: 600,
                      fontSize: '0.82rem',
                      mb: 2,
                    }}
                  >
                    {category.product_count} items available
                  </Typography>

                  <Box
                    sx={{
                      position: 'relative',
                      zIndex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#ff6d6d',
                        fontWeight: 700,
                        fontSize: '0.88rem',
                      }}
                    >
                      Explore category
                    </Typography>
                    <ArrowForwardRoundedIcon sx={{ color: '#ff6d6d', fontSize: 18 }} />
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
}
